import React, { useEffect } from "react";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { useForm } from "@/src/provider/Form/FormProvider";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import Person from "@/src/components/Form/Common/Person/Person";
import Address from "@/src/components/Form/Common/Address/Address";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import { isIban, isPhoneNumber, isRequired, minStringLength, requiredFieldDefault, requiredValidator } from "@/src/services/validationRules";
import InputAdornment from "@mui/material/InputAdornment";
import { IbanInput } from "@/src/components/FormFields/MaskedInputs/IbanInput";
import Group from "@/src/components/Wizard/Group";
import { translate } from "@/src/services/translations/translations";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import TextFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TextFieldConfigured/TextFieldConfigured";
import DateFieldConfigured from "@/src/components/Form/ConfiguredFormElements/DateFieldConfigured/DateFieldConfigured";
import TimeFieldConfigured from "@/src/components/Form/ConfiguredFormElements/TimeFieldConfigured/TimeFieldConfigured";
import IconTrafficAccident from "@/src/assets/icons/trafficLaw/toggleable/IconTrafficAccident";
import IconCarDamage from "@/src/assets/icons/trafficLaw/toggleable/IconCarDamage";
import IconCarAndPersonDamage from "@/src/assets/icons/trafficLaw/toggleable/IconCarAndPersonDamage";
import IconObstacleDamage from "@/src/assets/icons/trafficLaw/toggleable/IconObstacleDamage";
import IconSidewaysCollision from "@/src/assets/icons/trafficLaw/toggleable/IconSidewaysCollision";
import IconParkingCollision from "@/src/assets/icons/trafficLaw/toggleable/IconParkingCollision";
import IconHospital from "@/src/assets/icons/generic/toggleable/IconHospital";
import IconQuestionmark from "@/src/assets/icons/common/toggleable/IconQuestionmark";
import HighlightBox from "@/src/components/Box/HighlightBox/HighlightBox";

type AccidentBaseProps = {
  submitProps: FormSubmitProps;
};

export default function AccidentBase({ submitProps }: AccidentBaseProps) {
  const { handleSubmit, values, registerValidators, handleChange } = useForm();

  useEffect(() => {
    if (["vehicle", "vehicleAndPerson"].includes(values.typeOfDamage)) {
      registerValidators("clientIsVehicleOwner", [
        {
          validator: isRequired,
          message: "Bitte wählen Sie eine der angezeigten Antworten aus",
        },
      ]);
      return;
    }
    registerValidators("clientIsVehicleOwner", []);
  }, [values.typeOfDamage]);

  useEffect(() => {
    if (values.hasPreciseAccidentAddress === "no") {
      handleChange({
        target: {
          name: "accidentAddress.streetAddress",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "accidentAddress.postalCode",
          value: null,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "accidentAddress.addressLocality",
          value: null,
          type: "text",
        },
      });
    }
  }, [values.hasPreciseAccidentAddress]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Grid
            container
            spacing={2}
            sx={{
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Person path="client" headline={"Wie heißen Sie?"} />
            <Grid size={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            <Address path="client.residenceAddress" headline={"Wo wohnen Sie?"} />
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{translate("trafficAccident.client.bankData.iban.question", values)}</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                label={translate("trafficAccident.client.bankData.iban.label", values)}
                name="client.bankData.iban"
                validators={[
                  {
                    validator: isIban,
                    message: "Die eingegebene IBAN ist ungültig",
                  },
                ]}
                helperText={translate("trafficAccident.client.bankData.iban.helperText", values)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">DE</InputAdornment>,
                  inputComponent: IbanInput,
                }}
              />
            </Grid>
            <Grid size={12}>
              <Group
                type="radio"
                name="typeOfDamage"
                question={translate("trafficAccident.typeOfDamage.question", values)}
                options={[
                  {
                    labelText: translate("trafficAccident.typeOfDamage.options.vehicle.label", values),
                    labelIcon: <IconCarDamage />,
                    labelIconActive: <IconCarDamage active />,
                    value: "vehicle",
                    optionHintText: translate("trafficAccident.typeOfDamage.options.vehicle.optionHintText", values),
                  },
                  {
                    labelText: translate("trafficAccident.typeOfDamage.options.person.label", values),
                    labelIcon: <IconHospital sx={{ zoom: 0.8 }} />,
                    labelIconActive: <IconHospital sx={{ zoom: 0.8 }} active />,
                    value: "person",
                    optionHintText: translate("trafficAccident.typeOfDamage.options.person.optionHintText", values),
                  },
                  {
                    labelText: translate("trafficAccident.typeOfDamage.options.vehicleAndPerson.label", values),
                    labelIcon: <IconCarAndPersonDamage />,
                    labelIconActive: <IconCarAndPersonDamage active />,
                    value: "vehicleAndPerson",
                  },
                  {
                    labelText: translate("trafficAccident.typeOfDamage.options.other.label", values),
                    labelIcon: <IconObstacleDamage />,
                    labelIconActive: <IconObstacleDamage active />,
                    value: "other",
                    optionHintText: translate("trafficAccident.typeOfDamage.options.other.optionHintText", values),
                  },
                ]}
                validators={[requiredFieldDefault]}
              />
            </Grid>
            {["vehicle", "vehicleAndPerson"].includes(values.typeOfDamage) && (
              <Grid size={12}>
                <Group
                  type="radio"
                  name="clientIsVehicleOwner"
                  question={translate("trafficAccident.clientIsVehicleOwner.question", values)}
                  modalContent={
                    <Typography sx={{
                      marginBottom: "16px"
                    }}>
                      Um Schadenersatzansprüche aus einem Verkehrsunfall geltend zu machen, müssen Sie mindestens eine der beiden Rollen erfüllen. Der
                      Eigentümer ist die Person, die im Fahrzeugbrief eingetragen ist, während der Halter die im Fahrzeugschein vermerkte Person ist.
                    </Typography>
                  }
                  options={[
                    {
                      labelText: translate("trafficAccident.clientIsVehicleOwner.options.yes.label", values),
                      labelIcon: <CheckIcon />,
                      labelIconActive: <CheckIcon active />,
                      value: "yes",
                    },
                    {
                      labelText: translate("trafficAccident.clientIsVehicleOwner.options.no.label", values),
                      labelIcon: <CrossIcon />,
                      labelIconActive: <CrossIcon active />,
                      value: "no",
                    },
                  ]}
                  validators={[
                    {
                      validator: isRequired,
                      message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                    },
                  ]}
                />
              </Grid>
            )}
            {values.clientIsVehicleOwner === "no" && (
              <Grid size={12}>
                <HighlightBox>
                  <Typography sx={{
                    color: "error.main"
                  }}>
                    Um Schadenersatzansprüche aus einem Verkehrsunfall geltend zu machen, müssen Sie Halter und/oder Eigentümer des beschädigten Fahrzeugs sein.
                    Daher können Sie Legalbird leider nicht mit der rechtlichen Durchsetzung Ihrer Ansprüche beauftragen. Bitte setzen Sie sich mit dem Halter
                    oder Eigentümer in Verbindung und bitten Sie diesen, den Rechtsfall anzulegen. Dann werden wir gerne umgehend für Sie tätig!
                  </Typography>
                </HighlightBox>
              </Grid>
            )}
            {values.clientIsVehicleOwner !== "no" && (
              <>
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="typeOfAccident"
                    question={translate("trafficAccident.typeOfAccident.question", values)}
                    options={[
                      {
                        labelText: translate("trafficAccident.typeOfAccident.options.rearEndCollision.label", values),
                        labelIcon: <IconTrafficAccident />,
                        labelIconActive: <IconTrafficAccident active />,
                        value: "rearEndCollision",
                      },
                      {
                        labelText: translate("trafficAccident.typeOfAccident.options.laneChangeCollision.label", values),
                        labelIcon: <IconSidewaysCollision />,
                        labelIconActive: <IconSidewaysCollision active />,
                        value: "laneChangeCollision",
                      },
                      {
                        labelText: translate("trafficAccident.typeOfAccident.options.parking.label", values),
                        labelIcon: <IconParkingCollision />,
                        labelIconActive: <IconParkingCollision active />,
                        value: "parking",
                      },
                      {
                        labelText: translate("trafficAccident.typeOfAccident.options.other.label", values),
                        labelIcon: <IconQuestionmark />,
                        labelIconActive: <IconQuestionmark active />,
                        value: "other",
                      },
                    ]}
                    validators={[requiredFieldDefault]}
                  />
                </Grid>
                <TextFieldConfigured caseData={values} fieldPath={"courseOfEvents"} required multiline />
                <DateFieldConfigured caseData={values} fieldPath={"accidentDate"} required />
                <TimeFieldConfigured caseData={values} fieldPath={"accidentTime"} required />
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="hasPreciseAccidentAddress"
                    question={translate("trafficAccident.hasPreciseAccidentAddress.question", values)}
                    options={[
                      {
                        labelText: translate("trafficAccident.hasPreciseAccidentAddress.options.yes.label", values),
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                      {
                        labelText: translate("trafficAccident.hasPreciseAccidentAddress.options.no.label", values),
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                    ]}
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                      },
                    ]}
                  />
                </Grid>
                {values.hasPreciseAccidentAddress === "yes" && <Address path="accidentAddress" headline={"Unfallort"} />}
                {values.hasPreciseAccidentAddress === "no" && <TextFieldConfigured caseData={values} fieldPath={"accidentLocalityDescription"} multiline />}
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="policeInvolved"
                    question={translate("trafficAccident.policeInvolved.question", values)}
                    options={[
                      {
                        labelText: translate("trafficAccident.policeInvolved.options.yes.label", values),
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                      {
                        labelText: translate("trafficAccident.policeInvolved.options.no.label", values),
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                    ]}
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte wählen Sie eine der angezeigten Antworten aus",
                      },
                    ]}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {values.clientIsVehicleOwner !== "no" && <FormSubmit {...submitProps} />}
        </Box>
      </FormFader>
    </Paper>
  );
}
